/**
 * 全系统对人呈现的时间统一为**工厂时区**（租户元数据 factory_timezone · 默认北京时间 Asia/Shanghai），
 * 不按浏览器所在时区渲染（2026-09-19 用户要求：全系统统一北京时间）。
 * 渲染时刻一律走本文件的 formatFactoryDateTime / formatFactoryTime / formatFactoryDate；
 * `new Date(x).toLocaleString()` 一类按浏览器时区的写法被 eslint（no-restricted-syntax）拦下。
 */
export const DEFAULT_FACTORY_TIMEZONE = "Asia/Shanghai";

type FactoryTimeValue = string | Date | null | undefined;

function validDate(value: FactoryTimeValue): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Resolve an IANA time-zone name without letting a malformed tenant setting
 * break rendering. The factory fallback is shared by all CNC data pages.
 */
export function resolveFactoryTimezone(timeZone?: string | null): string {
  const candidate = timeZone?.trim() || DEFAULT_FACTORY_TIMEZONE;
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: candidate }).format(0);
    return candidate;
  } catch {
    return DEFAULT_FACTORY_TIMEZONE;
  }
}

export function formatFactoryDateTime(
  value: FactoryTimeValue,
  timeZone?: string | null,
): string {
  const date = validDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: resolveFactoryTimezone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

export function formatFactoryTime(
  value: FactoryTimeValue,
  timeZone?: string | null,
): string {
  const date = validDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: resolveFactoryTimezone(timeZone),
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

/** 只到日期（YYYY/MM/DD）· 用于「创建于」这类不需要时分的场合，同样按工厂时区取日。 */
export function formatFactoryDate(
  value: FactoryTimeValue,
  timeZone?: string | null,
): string {
  const date = validDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: resolveFactoryTimezone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** 到分钟（YYYY/MM/DD HH:mm）· 列表里省一列宽度时用。 */
export function formatFactoryDateTimeMinute(
  value: FactoryTimeValue,
  timeZone?: string | null,
): string {
  const date = validDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: resolveFactoryTimezone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

/** 时区给人看的叫法：北京时间直接说「北京时间」，别的给 IANA 名（如 Europe/Berlin）。 */
export function factoryTimezoneLabel(timeZone?: string | null): string {
  const resolved = resolveFactoryTimezone(timeZone);
  return resolved === DEFAULT_FACTORY_TIMEZONE ? "北京时间" : resolved;
}
